
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useOrganization } from '@/contexts/OrganizationContext';
import { toast } from '@/hooks/use-toast';
import type { UsageMeter } from '@/lib/limitErrors';

export interface Subscription {
  id: string;
  organization_id: string;
  plan_name: string;
  status: 'trialing' | 'active' | 'past_due' | 'canceled';
  trial_ends_at: string | null;
  billing_interval: 'monthly' | 'yearly' | 'custom';
  grace_ends_at: string | null;
  influencer_limit: number;
  campaign_limit: number;
  team_member_limit: number;
  deliverable_limit: number;
  profile_refresh_limit: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface UsageStats {
  organization_id: string | null;
  influencer_count: number | null;
  campaign_count: number | null;
  team_member_count: number | null;
  deliverable_count: number | null;
  profile_refresh_count: number | null;
}

export function useSubscription() {
  const { currentOrganization } = useOrganization();

  return useQuery<Subscription | null>({
    queryKey: ['subscription', currentOrganization?.id],
    queryFn: async () => {
      if (!currentOrganization?.id) {
        throw new Error('No organization selected');
      }

      const { data, error } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('organization_id', currentOrganization.id)
        .maybeSingle();

      if (error) {
        console.error('Error fetching subscription:', error);
        throw error;
      }

      return (data as Subscription) || null;
    },
    enabled: !!currentOrganization?.id,
  });
}

export function useUsageStats() {
  const { currentOrganization } = useOrganization();

  return useQuery<UsageStats | null>({
    queryKey: ['usage-stats', currentOrganization?.id],
    queryFn: async () => {
      if (!currentOrganization?.id) {
        throw new Error('No organization selected');
      }

      const { data, error } = await supabase
        .from('usage_stats')
        .select('*')
        .eq('organization_id', currentOrganization.id)
        .maybeSingle();

      if (error) {
        console.error('Error fetching usage stats:', error);
        throw error;
      }

      return (data as UsageStats) || null;
    },
    enabled: !!currentOrganization?.id,
  });
}

/**
 * Client-side usage-limit checks for preemptive UX (disabling actions,
 * showing upgrade prompts before a request is made). Fails open while data
 * is loading or on a negative (unlimited) limit — the database triggers are
 * the actual enforcement and reject anything that slips through.
 */
export function useUsageLimits() {
  const { data: subscription } = useSubscription();
  const { data: usage } = useUsageStats();

  const isAtLimit = (meter: UsageMeter): boolean => {
    if (!subscription || !usage) return false;

    const usageByMeter: Record<UsageMeter, { used: number; limit: number }> = {
      influencers: {
        used: usage.influencer_count ?? 0,
        limit: subscription.influencer_limit,
      },
      campaigns: {
        used: usage.campaign_count ?? 0,
        limit: subscription.campaign_limit,
      },
      team_members: {
        used: usage.team_member_count ?? 0,
        limit: subscription.team_member_limit,
      },
      deliverables: {
        used: usage.deliverable_count ?? 0,
        limit: subscription.deliverable_limit,
      },
      profile_refreshes: {
        used: usage.profile_refresh_count ?? 0,
        limit: subscription.profile_refresh_limit,
      },
    };

    const { used, limit } = usageByMeter[meter];
    if (limit < 0) return false;
    return used >= limit;
  };

  return { isAtLimit };
}

export interface SubscriptionUpgradeRequest {
  id: string;
  organization_id: string;
  user_id: string;
  requested_plan: string;
  billing_interval: 'monthly' | 'yearly' | 'custom';
  notes: string | null;
  status: 'pending' | 'approved' | 'rejected' | 'completed';
  resolved_at: string | null;
  resolved_by: string | null;
  admin_notes: string | null;
  created_at: string;
  updated_at: string;
}

export function usePendingUpgradeRequest() {
  const { currentOrganization } = useOrganization();

  return useQuery<SubscriptionUpgradeRequest | null>({
    queryKey: ['pending-upgrade-request', currentOrganization?.id],
    queryFn: async () => {
      if (!currentOrganization?.id) return null;

      const { data, error } = await supabase
        .from('subscription_upgrade_requests')
        .select('*')
        .eq('organization_id', currentOrganization.id)
        .eq('status', 'pending')
        .maybeSingle();

      if (error) {
        console.error('Error fetching pending upgrade request:', error);
        throw error;
      }

      return (data as SubscriptionUpgradeRequest) || null;
    },
    enabled: !!currentOrganization?.id,
  });
}

export function useRequestUpgrade() {
  const queryClient = useQueryClient();
  const { currentOrganization } = useOrganization();

  return useMutation({
    mutationFn: async ({
      planName,
      billingInterval,
      notes,
    }: {
      planName: string;
      billingInterval: string;
      notes?: string;
    }) => {
      const { data, error } = await supabase.rpc('request_subscription_upgrade', {
        p_plan_name: planName,
        p_billing_interval: billingInterval,
        p_notes: notes || null,
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['pending-upgrade-request', currentOrganization?.id],
      });
      toast({
        title: 'Upgrade Request Submitted',
        description: 'Our team will review your request and confirm your plan upgrade shortly.',
      });
    },
    onError: (err: any) => {
      toast({
        title: 'Request Failed',
        description: err.message || 'Could not submit upgrade request.',
        variant: 'destructive',
      });
    },
  });
}
